import { useNavigationState } from "@/zustand/NavigationState";

import FileAttachmentContainer from "./FileAttachmentContainer";
import FileAttachmentLoading from "./FileAttachmentLoading";
import FileAttachmentError from "./FileAttachmentError";
import FileAttachmentEmpty from "./FileAttachmentEmpty";
import FileAttachment from "./FileAttachment";
import FileAttachmentAnchor from "./FileAttachmentAnchor";
import { useFileAttachments } from "./useFileAttachments";

/**
 * FileAttachmentList component that displays file attachments with infinite loading
 * Shows file attachments in a list layout with newest at the top
 * Loads more attachments when scrolling to the top
 *
 * This component orchestrates the various subcomponents and manages the overall
 * file attachment display flow.
 */
export default function FileAttachmentList() {
  // Extract only the state values we need from navigation state
  const activeChatId = useNavigationState((state) => state.activeChatId);

  // Use the custom hook to manage file attachments
  const {
    isLoading,
    error,
    fileMessages,
    isFullFileListLoaded,
    scrollerRef,
    filesListRef,
    anchorRef,
    handleRetry,
  } = useFileAttachments(activeChatId);

  // Return null if no active chat
  if (!activeChatId) {
    return null;
  }

  return (
    <FileAttachmentContainer
      scrollerRef={scrollerRef}
      filesListRef={filesListRef}
    >
      {/* Error state */}
      {error && <FileAttachmentError onRetry={handleRetry} />}
      {/* Display files in a flat list with newest at the top */}
      {
        <div className="flex w-full flex-col gap-1">
          {fileMessages.map((message) => (
            <FileAttachment key={message._clientId} message={message} />
          ))}
        </div>
      }
      {/* Empty state - only show when not loading and no error */}
      {!isLoading && !error && fileMessages.length === 0 && (
        <FileAttachmentEmpty />
      )}

      {isLoading && <FileAttachmentLoading />}

      <FileAttachmentAnchor
        anchorRef={anchorRef}
        isFullListLoaded={isFullFileListLoaded}
        hasAttachments={fileMessages.length > 0}
      />
    </FileAttachmentContainer>
  );
}
